import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { accountDeletionService } from '@/services/api/accountDeletion.service';
import { parseApiError } from '@/services/api/quotes-http';
import { deletionStatusKey } from './hooks';

/** Cancels the pending deletion. A 404 means it is already gone (cancelled elsewhere), which is the result asked for. */
export function CancelDeletionButton({ label, variant = 'outline' }: { label?: string; variant?: 'outline' | 'primary' }) {
  const { t } = useTranslation('accountDeletion');
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    setBusy(true);
    try {
      await accountDeletionService.cancel();
      toast({ title: t('banner.cancelled') });
    } catch (e) {
      const parsed = parseApiError(e);
      if (parsed.status === 404) toast({ title: t('banner.cancelled') });
      else toast({ title: parsed.code === 'purge_started' ? t('banner.tooLate') : t('banner.cancelFailed'), variant: 'destructive' });
    } finally {
      setBusy(false);
      await client.invalidateQueries({ queryKey: deletionStatusKey });
    }
  };
  return (
    <Button variant={variant} size="md" loading={busy} disabled={busy} onClick={() => void cancel()}>
      {label ?? t('banner.cancel')}
    </Button>
  );
}
